import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Alert, Dropdown, Badge } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { supabase } from '../lib/supabaseClient';
import { compressImageForUpload } from '../lib/compressImage';
import ConfirmDialog from '../components/ConfirmDialog';
import './AdminDashboard.css';

function AdminTeam() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
  const [uploading, setUploading] = useState(false);
  const [sortBy, setSortBy] = useState('displayOrder');
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger',
  });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    positions: '',
    image_url: '',
    is_founder: false,
  });

  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setTeams(data || []);
    } catch (error) {
      showAlert('Error fetching teams: ' + error.message, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, variant = 'success') => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 3000);
  };

  const handleShowModal = (team = null) => {
    if (team) {
      setEditMode(true);
      setCurrentTeam(team);
      setFormData({
        first_name: team.first_name,
        last_name: team.last_name || '',
        positions: Array.isArray(team.positions) ? team.positions.join(', ') : team.positions,
        image_url: team.image_url || '',
        is_founder: team.is_founder || false,
      });
    } else {
      setEditMode(false);
      setCurrentTeam(null);
      setFormData({
        first_name: '',
        last_name: '',
        positions: '',
        image_url: '',
        is_founder: false,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setCurrentTeam(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showAlert('Please upload only JPG, PNG, or WebP images', 'warning');
      return;
    }

    try {
      setUploading(true);

      const fileToUpload = await compressImageForUpload(file);

      const fileExt = fileToUpload.name.split('.').pop() || 'jpg';
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error } = await supabase.storage
        .from('team')
        .upload(filePath, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      const { data: publicUrlData } = supabase.storage
        .from('team')
        .getPublicUrl(filePath);

      const imageUrl = publicUrlData.publicUrl;

      setFormData({ 
        ...formData, 
        image_url: imageUrl 
      });

      showAlert('Image uploaded successfully!', 'success');
    } catch (error) {
      showAlert('Error uploading image: ' + error.message, 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name || !formData.positions) {
      showAlert('Please fill all required fields (First Name, Positions)', 'warning');
      return;
    }

    try {
      const positionsArray = formData.positions
        .split(',')
        .map(p => p.trim())
        .filter(p => p);

      const teamData = {
        first_name: formData.first_name,
        last_name: formData.last_name || null,
        positions: positionsArray,
        image_url: formData.image_url || null,
        is_founder: formData.is_founder,
      };

      if (editMode) {
        const { error } = await supabase
          .from('teams')
          .update({
            ...teamData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', currentTeam.id);

        if (error) throw error;
        showAlert('Team member updated successfully!', 'success');
      } else {
        const maxOrder = teams.length > 0 
          ? Math.max(...teams.map(t => t.display_order || 0)) 
          : 0;

        const { error } = await supabase
          .from('teams')
          .insert([{
            ...teamData,
            display_order: maxOrder + 1,
          }]);

        if (error) throw error;
        showAlert('Team member added successfully!', 'success');
      }

      handleCloseModal();
      fetchTeams();
    } catch (error) {
      showAlert('Error saving team: ' + error.message, 'danger');
    }
  };

  const handleDelete = async (id, name) => {
    setConfirmDialog({
      show: true,
      title: 'Delete Team Member',
      message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('teams')
            .delete()
            .eq('id', id);

          if (error) throw error;
          showAlert('Team member deleted successfully!', 'success');
          fetchTeams();
        } catch (error) {
          showAlert('Error deleting team: ' + error.message, 'danger');
        }
      },
      variant: 'danger',
    });
  };

  const getSortedTeams = () => {
    const sorted = [...teams];
    if (sortBy === 'name') {
      return sorted.sort((a, b) => a.first_name.localeCompare(b.first_name));
    } else if (sortBy === 'lastAdded') {
      return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'displayOrder') {
      return sorted.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    }
    return sorted;
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(getSortedTeams());
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = items.map((item, index) => ({
      ...item,
      display_order: index,
    }));

    setTeams(updatedItems);

    try {
      for (const item of updatedItems) {
        await supabase
          .from('teams')
          .update({ display_order: item.display_order })
          .eq('id', item.id);
      }
      showAlert('Order updated successfully!', 'success');
    } catch (error) {
      showAlert('Error updating order: ' + error.message, 'danger');
      fetchTeams();
    }
  };

  return (
    <div className="admin-dashboard">
      <Container fluid className="py-4">
        <div className="admin-header">
          <div>
            <h1 className="admin-title">Team Management</h1>
            <p className="admin-subtitle">Manage team members and founders</p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" id="sort-dropdown">
                <i className="bi bi-sort-down me-2"></i>
                Sort: {sortBy === 'name' ? 'Name' : sortBy === 'lastAdded' ? 'Last Added' : 'Display Order'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setSortBy('displayOrder')}>
                  <i className="bi bi-list-ol me-2"></i>
                  Display Order
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('name')}>
                  <i className="bi bi-sort-alpha-down me-2"></i>
                  Sort by Name
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setSortBy('lastAdded')}>
                  <i className="bi bi-clock-history me-2"></i>
                  Sort by Last Added
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Team Member
            </Button>
          </div>
        </div>

        {alert.show && (
          <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false })}>
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Table striped bordered hover className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th style={{ width: '80px' }}>Image</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Positions</th>
                    <th style={{ width: '100px' }}>Role</th>
                    <th style={{ width: '80px' }}>Order</th>
                    <th style={{ width: '150px' }}>Actions</th>
                  </tr>
                </thead>
                <Droppable droppableId="teams-table">
                  {(provided) => (
                    <tbody
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {teams.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            No team members found. Add your first team member!
                          </td>
                        </tr>
                      ) : (
                        getSortedTeams().map((team, index) => (
                          <Draggable
                            key={team.id}
                            draggableId={String(team.id)}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={snapshot.isDragging ? 'dragging-row' : ''}
                              >
                                <td
                                  {...provided.dragHandleProps}
                                  className="drag-handle"
                                  title="Drag to reorder"
                                >
                                  <i className="bi bi-grip-vertical"></i>
                                </td>
                                <td>
                                  <img
                                    src={team.image_url}
                                    alt={team.first_name}
                                    className="model-thumb"
                                  />
                                </td>
                                <td>{team.first_name}</td>
                                <td>{team.last_name || '-'}</td>
                                <td>
                                  {Array.isArray(team.positions) 
                                    ? team.positions.join(', ') 
                                    : team.positions}
                                </td>
                                <td>
                                  {team.is_founder ? (
                                    <Badge bg="warning" text="dark">
                                      <i className="bi bi-star-fill me-1"></i>
                                      Founder
                                    </Badge>
                                  ) : (
                                    <Badge bg="secondary">Member</Badge>
                                  )}
                                </td>
                                <td className="text-center">{team.display_order}</td>
                                <td>
                                  <div className="action-buttons">
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleShowModal(team)}
                                      title="Edit"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleDelete(team.id, team.first_name)}
                                      title="Delete"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </Table>
            </DragDropContext>
          </div>
        )}
      </Container>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? 'Edit Team Member' : 'Add New Team Member'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter first name"
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    placeholder="Enter last name (optional)"
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Positions * <small className="text-muted">(separate with comma)</small></Form.Label>
              <Form.Control
                type="text"
                name="positions"
                value={formData.positions}
                onChange={handleInputChange}
                required
                placeholder="e.g. Graphic Designer, Content Creator"
              />
              <Form.Text className="text-muted">
                If multiple positions, separate with comma: "Designer, Photographer"
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Team Photo 
                <small className="text-muted ms-2">(Upload or paste URL)</small>
              </Form.Label>
              <div className="image-upload-group">
                <Form.Control
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="mb-2"
                />
                <div className="or-divider">OR</div>
                <Form.Control
                  type="url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/image.jpg"
                  disabled={uploading}
                />
                <Form.Text className="text-muted d-block mt-2">
                  <i className="bi bi-cloud-upload me-1"></i>
                  Images will be uploaded to <strong>team</strong> bucket in Supabase Storage
                </Form.Text>
                {formData.image_url && (
                  <div className="image-preview mt-2">
                    <img src={formData.image_url} alt="Preview" />
                  </div>
                )}
              </div>
            </Form.Group>

            {uploading && (
              <div className="text-center mb-3">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                  <span className="visually-hidden">Uploading...</span>
                </div>
                <span className="text-muted">Uploading image...</span>
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="is_founder"
                checked={formData.is_founder}
                onChange={handleInputChange}
                label="Mark as Founder (will be displayed prominently)"
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editMode ? 'Update Team Member' : 'Add Team Member'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        show={confirmDialog.show}
        onHide={() => setConfirmDialog({ ...confirmDialog, show: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
      />
    </div>
  );
}

export default AdminTeam;
